// ----------------------------------------------------------------------

// The Careers caseload table's filters, sort and attention tiles (#612, #613). The server sends one flat list of students with the derived
// fields (GET /api/career/caseload); everything here works on that list.
// Filters and sort are kept in the page's query string, so the back button
// returns a counsellor to the same view after they open a student.

import { VISIT_STATUS } from './visit-status';

export const EMPTY_FILTERS = {
  q: '', // name or ASN
  grades: [], // numbers
  graduating: false,
  visit: [], // visit statuses
  seen: '', // '30' | '60' | '90' | 'never'
  contact: '', // no contact of any kind in N days
  program: '', // 'any' or a program code
  referral: false, // a referral is pending
  placement: false, // a placement is active
  agreement: false, // the agreement isn't signed
  ending: false, // a placement ends within 30 days
  todos: '', // 'open' | 'overdue'
  progress: '', // under N percent of the checklist
  pathway: '', // a pathway, or 'none'
  prereq: '', // 'HCS3000' | 'AGR3000' not yet done
  interest: '',
  flags: false,
  industry: '',
};

export const ENDING_SOON_DAYS = 30;

export const SEEN_OPTIONS = [
  { value: '30', label: '30+ days' },
  { value: '60', label: '60+ days' },
  { value: '90', label: '90+ days' },
  { value: 'never', label: 'Never visited' },
];

export const VISIT_STATUS_OPTIONS = [
  { value: VISIT_STATUS.OVERDUE, label: 'Overdue' },
  { value: VISIT_STATUS.DUE_SOON, label: 'Due soon' },
  { value: VISIT_STATUS.PLANNED, label: 'Planned' },
  { value: VISIT_STATUS.NO_PLAN, label: 'No plan' },
  { value: VISIT_STATUS.NO_FOLLOW_UP, label: 'No follow-up needed' },
];

// The query string's names for each filter
const FLAG_KEYS = ['graduating', 'referral', 'placement', 'agreement', 'ending', 'flags'];
const TEXT_KEYS = ['q', 'seen', 'contact', 'program', 'todos', 'progress', 'pathway', 'prereq', 'interest', 'industry'];

export const DEFAULT_SORT = { sort: 'nextVisit', dir: 'asc' };

/** { filters, sort, dir } from the page's URLSearchParams; anything missing or unknown is the default. */
export function parseCaseloadParams(params) {
  const filters = { ...EMPTY_FILTERS };
  TEXT_KEYS.forEach((key) => {
    filters[key] = params.get(key) || '';
  });
  FLAG_KEYS.forEach((key) => {
    filters[key] = params.get(key) === '1';
  });
  filters.grades = (params.get('grade') || '')
    .split(',')
    .map(Number)
    .filter((n) => Number.isInteger(n) && n >= 9 && n <= 12);
  const statuses = VISIT_STATUS_OPTIONS.map((o) => o.value);
  filters.visit = (params.get('visit') || '').split(',').filter((v) => statuses.includes(v));
  const dir = params.get('dir');
  return {
    filters,
    sort: params.get('sort') || DEFAULT_SORT.sort,
    dir: dir === 'asc' || dir === 'desc' ? dir : DEFAULT_SORT.dir,
  };
}

/**
 * The query string for a view: `base` (the params to keep, e.g. the school)
 * plus whatever differs from the defaults, so a plain view has a plain URL.
 */
export function caseloadParams({ filters, sort, dir }, base = new URLSearchParams()) {
  const params = new URLSearchParams(base);
  [...TEXT_KEYS, ...FLAG_KEYS, 'grade', 'visit', 'sort', 'dir'].forEach((key) => params.delete(key));
  TEXT_KEYS.forEach((key) => {
    if (filters[key]) params.set(key, filters[key]);
  });
  FLAG_KEYS.forEach((key) => {
    if (filters[key]) params.set(key, '1');
  });
  if (filters.grades.length) params.set('grade', filters.grades.join(','));
  if (filters.visit.length) params.set('visit', filters.visit.join(','));
  if (sort !== DEFAULT_SORT.sort || dir !== DEFAULT_SORT.dir) {
    params.set('sort', sort);
    params.set('dir', dir);
  }
  return params;
}

const sameFilters = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/** How many filters are on. */
export function activeFilterCount(filters) {
  return Object.keys(EMPTY_FILTERS).filter((key) => !sameFilters(filters[key], EMPTY_FILTERS[key])).length;
}

/** The rows that match every filter that is on. */
export function applyCaseloadFilters(rows, filters) {
  const f = { ...EMPTY_FILTERS, ...filters };
  const q = f.q.trim().toLowerCase();
  const seen = f.seen && f.seen !== 'never' ? Number(f.seen) : null;
  const contact = f.contact ? Number(f.contact) : null;
  const progress = f.progress ? Number(f.progress) : null;

  return rows.filter((r) => {
    if (q && !r.name.toLowerCase().includes(q) && !String(r.asn).includes(q)) return false;
    if (f.grades.length && !f.grades.includes(r.grade)) return false;
    if (f.graduating && !r.graduating) return false;
    if (f.visit.length && !f.visit.includes(r.visitStatus)) return false;
    if (f.seen === 'never' && r.daysSinceVisit !== null) return false;
    if (seen !== null && r.daysSinceVisit !== null && r.daysSinceVisit < seen) return false;
    if (contact !== null && r.daysSinceContact !== null && r.daysSinceContact < contact) return false;
    if (f.program === 'any' && !r.programs.length) return false;
    if (f.program && f.program !== 'any' && !r.programs.includes(f.program)) return false;
    if (f.referral && !r.referralPending) return false;
    if (f.placement && !r.placementActive) return false;
    if (f.agreement && !r.agreementUnsigned) return false;
    if (f.ending && !(r.placementEndsInDays !== null && r.placementEndsInDays <= ENDING_SOON_DAYS)) return false;
    if (f.todos === 'open' && !r.openTodos) return false;
    if (f.todos === 'overdue' && !r.overdueTodos) return false;
    if (progress !== null && !(r.checklistPercent < progress)) return false;
    if (f.pathway === 'none' && r.pathway) return false;
    if (f.pathway && f.pathway !== 'none' && r.pathway !== f.pathway) return false;
    if (f.prereq === 'HCS3000' && !r.missingHCS3000) return false;
    if (f.prereq === 'AGR3000' && !r.missingAGR3000) return false;
    if (f.interest && !r.interests.includes(f.interest)) return false;
    if (f.flags && !r.flags.length) return false;
    if (f.industry && r.industry !== f.industry) return false;
    return true;
  });
}

// Planned visits by day, overdue first; then no plan; then no follow-up needed
const NEXT_VISIT_RANK = {
  [VISIT_STATUS.OVERDUE]: 0,
  [VISIT_STATUS.DUE_SOON]: 1,
  [VISIT_STATUS.PLANNED]: 2,
  [VISIT_STATUS.NO_PLAN]: 3,
  [VISIT_STATUS.NO_FOLLOW_UP]: 4,
};

// Each sortable column's value, compared ascending; null sorts last whichever way
const SORT_VALUES = {
  name: (r) => r.name.toLowerCase(),
  grade: (r) => r.grade,
  nextVisit: (r) => `${NEXT_VISIT_RANK[r.visitStatus] ?? 5}${r.nextVisitBy || '9999-99-99'}`,
  lastVisit: (r) => r.daysSinceVisit, // never is last when ascending: it has been longest
  contact: (r) => r.daysSinceContact,
  program: (r) => r.programs.join(',') || null,
  todos: (r) => r.overdueTodos * 1000 + r.openTodos,
  progress: (r) => r.checklistPercent,
  flags: (r) => r.flags.length,
  pathway: (r) => (r.pathway ? r.pathway.toLowerCase() : null),
};

export const SORT_KEYS = Object.keys(SORT_VALUES);

/** The rows by a column; days since a visit sort with the longest first when descending, never counting as longest. */
export function sortCaseload(rows, sort = DEFAULT_SORT.sort, dir = DEFAULT_SORT.dir) {
  const value = SORT_VALUES[sort] || SORT_VALUES[DEFAULT_SORT.sort];
  const sign = dir === 'desc' ? -1 : 1;
  const never = (r) => (sort === 'lastVisit' || sort === 'contact') && value(r) === null;
  return [...rows].sort((a, b) => {
    const av = value(a);
    const bv = value(b);
    if (never(a) !== never(b)) return (never(a) ? 1 : -1) * sign;
    if (av === null || bv === null) {
      if (av === bv) return a.name.localeCompare(b.name);
      return av === null ? 1 : -1; // empty last, whichever way
    }
    let cmp = 0;
    if (av < bv) cmp = -1;
    else if (av > bv) cmp = 1;
    return cmp ? cmp * sign : a.name.localeCompare(b.name);
  });
}

// The attention tiles: a count each, and the filters it applies when clicked.
// They double as the page's quick views. Each answers a different question, so
// they don't overlap: anything narrower (60+ days, referral pending) is under
// More filters.
export const ATTENTION_TILES = [
  {
    id: 'visitsDue',
    label: 'Visits due',
    hint: 'Next visit is overdue or within 14 days',
    color: 'error',
    filters: { visit: [VISIT_STATUS.OVERDUE, VISIT_STATUS.DUE_SOON] },
  },
  { id: 'noPlan', label: 'No plan', hint: 'No next visit set', color: 'warning', filters: { visit: [VISIT_STATUS.NO_PLAN] } },
  { id: 'neverVisited', label: 'Never visited', hint: 'No visit logged yet', color: 'warning', filters: { seen: 'never' } },
  { id: 'overdueTodos', label: 'Overdue to-dos', hint: 'An open to-do is past its due date', color: 'error', filters: { todos: 'overdue' } },
  { id: 'offCampus', label: 'Off-campus', hint: 'On a placement or referred', color: 'info', filters: { program: 'any' } },
  {
    id: 'grade12Incomplete',
    label: 'Grade 12 incomplete',
    hint: 'Grade 12 with the diploma or certificate checklist not done',
    color: 'warning',
    filters: { grades: [12], progress: '100' },
  },
];

/** How many students each tile counts, over the whole list. */
export const tileCounts = (rows) =>
  Object.fromEntries(ATTENTION_TILES.map((t) => [t.id, applyCaseloadFilters(rows, { ...EMPTY_FILTERS, ...t.filters }).length]));

/** The filters a tile applies, on their own. */
export const tileFilters = (tile) => ({ ...EMPTY_FILTERS, ...tile.filters });

/** Whether the filters are exactly this tile's. */
export const isTileActive = (tile, filters) => sameFilters({ ...EMPTY_FILTERS, ...filters }, tileFilters(tile));

/** The distinct values of a text field across the rows, sorted, for a filter's options. */
export const distinctValues = (rows, pick) =>
  [...new Set(rows.flatMap((r) => [].concat(pick(r) ?? [])).filter(Boolean))].sort((a, b) => a.localeCompare(b));
